// src/components/LevelRunner.jsx
// Shell component: routing, intro overlay, win/lose handling.
//
// Puzzle ownership:
//   Memory levels: generateGrid() is called ONCE here (or on reset).
//   The resulting { target, player } is stored in state and passed as props
//   to BOTH RememberOverlay and MemoryGame. This guarantees they always
//   show the exact same pattern.
//
// Responsibilities:
//   • Read level config
//   • Generate puzzle once on level start
//   • Manage intro / remember / game phases
//   • Handle win and lose navigation
//   • Render MemoryGame or RiddleGame as appropriate

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gsap } from 'gsap';
import '../pages/lvl1.css';
import '../pages/lvl2.css';
import { useSound } from '../hooks/useSound';
import { useProgress } from '../hooks/useProgress';
import { getLevelConfig } from '../utils/levelConfig';
import { generateGrid } from '../utils/randomGrid';

// Split components
import RememberOverlay from '../components/game/RememberOverlay';
import MemoryGame from '../components/game/MemoryGame';
import RiddleGame from '../components/game/RiddleGame';

// ─── LevelRunner ──────────────────────────────────────────────────────────────
const LevelRunner = () => {
  const { id } = useParams();
  const level   = Number(id);
  const navigate = useNavigate();
  const { playHover, playClick, playVictory, playDefeat } = useSound();
  const { completeLevel } = useProgress();
  const config = getLevelConfig(level);

  // ── Phase flags ────────────────────────────────────────────────────────────
  const [closing,        setClosing]        = useState(false);
  const [showInfo,       setShowInfo]       = useState(true);
  const [showRemember,   setShowRemember]   = useState(false);
  const [hidingRemember, setHidingRemember] = useState(false);
  const [gameStarted,    setGameStarted]    = useState(false);
  const [gameOver,       setGameOver]       = useState(false);

  // ── Puzzle state (memory levels only) ─────────────────────────────────────
  // puzzleKey is incremented on reset to re-mount MemoryGame with fresh props.
  const [puzzle,     setPuzzle]     = useState(null);
  const [puzzleKey,  setPuzzleKey]  = useState(0);

  const contRef = useRef(null);

  // ── Early return guard (AFTER all hooks) ─────────────────────────────────
  // Placed after state declarations but before effects so hooks count is stable.
  const isValidConfig = Boolean(config);

  // ── Generate puzzle once when memory level starts ─────────────────────────
  // This runs exactly once per level load, and again only when puzzleKey changes (reset).
  useEffect(() => {
    if (!isValidConfig || config.type !== 'memory') return;
    const { colors, boardFilled } = config.difficulty;
    const fresh = generateGrid(colors, 9, boardFilled);
    setPuzzle(fresh);
  }, [puzzleKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: intentionally only depends on puzzleKey so it doesn't re-run if config changes.

  // ── Animate game section in when game actually starts ────────────────────
  // We watch `gameStarted` (not `showInfo`) because for memory levels the
  // section only mounts 500 ms after showInfo goes false. Watching showInfo
  // fires when the section is not yet in the DOM, so contRef.current is null.
  // requestAnimationFrame lets React flush the render before GSAP reads the ref.
  useEffect(() => {
    if (!gameStarted) return;
    requestAnimationFrame(() => {
      if (!contRef.current) return;
      gsap.fromTo(
        contRef.current,
        { opacity: 0, y: 20, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'power3.out', delay: 0.05 }
      );
    });
  }, [gameStarted]);

  // ── Remember overlay auto-dismiss ─────────────────────────────────────────
  useEffect(() => {
    if (!showRemember || !config) return;
    const previewMs = (config.difficulty.previewSeconds ?? 5) * 1000;

    const timer = setTimeout(() => {
      setHidingRemember(true);
      setTimeout(() => {
        setShowRemember(false);
        setHidingRemember(false);
        setGameStarted(true);
      }, 500);
    }, previewMs);

    return () => clearTimeout(timer);
  }, [showRemember, config]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const isMemory = config?.type === 'memory';

  const startPuzzle = () => {
    playClick();
    setClosing(true);
    setTimeout(() => {
      setShowInfo(false);
      if (isMemory) {
        setShowRemember(true);
      } else {
        setGameStarted(true);
      }
    }, 800);
  };

  const handleExit = () => {
    playClick();
    navigate(-1);
  };

  const handleWin = useCallback((earnedStars) => {
    if (!config) return;
    const earnedCoins = config.baseCoins + earnedStars * 5;
    completeLevel(level, earnedStars, earnedCoins);
    setGameOver(true);
    playVictory();
    setTimeout(() => {
      navigate('/victory', {
        state: { level, stars: earnedStars, coins: earnedCoins, replayPath: `/level/${level}` },
      });
    }, 1400);
  }, [config, completeLevel, level, navigate, playVictory]);

  const handleLose = useCallback(() => {
    setGameOver(true);
    playDefeat();
    setTimeout(() => {
      navigate('/defeat', { state: { level, replayPath: `/level/${level}` } });
    }, 1400);
  }, [level, navigate, playDefeat]);

  // Reset: regenerate puzzle and re-mount MemoryGame via key change
  const handleMemoryReset = () => {
    if (gameOver) return;
    setPuzzleKey(k => k + 1); // triggers the generateGrid useEffect above
  };

  // ── Guard: invalid level ───────────────────────────────────────────────────
  if (!isValidConfig) {
    return (
      <main className="level1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h1 style={{ color: 'var(--green)' }}>LEVEL NOT FOUND</h1>
      </main>
    );
  }

  const introData = getIntroData(level, config);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="level1">

      {/* ── INTRO OVERLAY ── */}
      {showInfo && (
        <div className={`levelinfo ${closing ? 'levelinfo-exit' : ''}`}>
          <div className="levelcont2 fade-up">
            <h1>LEVEL {level}</h1>
            <h4 className="lvlinfosub">{config.name}</h4>
          </div>

          <section className="levelinstructions">
            <div className={introData.charClass} />
            <article className={`${introData.missionClass} mission-enter`}>
              <h5 className={introData.titleClass}>MISSION</h5>
              <h5 className={introData.subClass}>{introData.sub}</h5>
              <h5 className={introData.infoClass}>{introData.info}</h5>
            </article>
          </section>

          <article className="btncontainer buttons-enter">
            <button className="btn1" onClick={startPuzzle} onMouseEnter={playHover}>
              START PUZZLE
            </button>
            <button className="btn2" onClick={() => { playClick(); navigate(-1); }} onMouseEnter={playHover}>
              BACK
            </button>
          </article>
        </div>
      )}

      {/* ── REMEMBER OVERLAY (memory levels only) ── */}
      {showRemember && isMemory && puzzle && (
        <RememberOverlay
          targetGrid={puzzle.target}
          previewSeconds={config.difficulty.previewSeconds ?? 5}
          hiding={hidingRemember}
        />
      )}

      {/* ── GAME CONTENT ── */}
      {!showInfo && gameStarted && (
        <section className="level1cont" ref={contRef}>
          <div className="lvl1-topbar">
            <button className="lvl1-exit-btn" onClick={handleExit} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              EXIT
            </button>
            <div className="lvl1-title-block">
              <h1>LEVEL {level}</h1>
            </div>
            <div style={{ width: 80 }} />
          </div>

          {isMemory && puzzle ? (
            /*
             * Key = puzzleKey: when the player hits Reset, puzzleKey increments,
             * React unmounts+remounts MemoryGame with the brand-new puzzle props.
             * This cleanly resets ALL local state inside MemoryGame without
             * needing complex sync logic.
             */
            <MemoryGame
              key={puzzleKey}
              targetGrid={puzzle.target}
              initialPlayer={puzzle.player}
              config={config}
              onWin={handleWin}
              onLose={handleLose}
              onReset={handleMemoryReset}
              gameOver={gameOver}
            />
          ) : (
            <RiddleGame
              config={config}
              onWin={handleWin}
              gameOver={gameOver}
            />
          )}
        </section>
      )}
    </main>
  );
};

// ─── Intro data helper ────────────────────────────────────────────────────────
/**
 * Returns CSS class names + mission copy for the intro overlay.
 * Keeps the overlay looking identical to the original lvl1/lvl2 intro screens.
 */
function getIntroData(level, config) {
  if (config.type === 'memory') {
    const seconds = config.difficulty.previewSeconds ?? 5;
    const moves   = config.difficulty.moves;
    return {
      charClass:    'missionchar1',
      missionClass: 'missionpink',
      titleClass:   'missiontitlepink',
      subClass:     'missionsubpink typing-sub',
      infoClass:    'missionipink typing-info',
      sub:  'Match the target pattern by swapping colored prisms!',
      info: `Memorize the color grid shown for ${seconds} seconds. Then recreate it by swapping prisms. Complete in ${moves} moves or fewer to win!`,
    };
  }
  return {
    charClass:    'missionchar222',
    missionClass: 'missionblue',
    titleClass:   'missiontitleblue',
    subClass:     'missionsubblue typing-sub',
    infoClass:    'missioniblue typing-info',
    sub:  'Read the riddle and arrange the characters in the correct order.',
    info: 'Each character wears a random number. Use the clues to find the correct order, drag them into place, then enter the code.',
  };
}

export default LevelRunner;